import type { ComponentChildren } from 'preact';
import { t } from '../i18n';

export function Spinner({ label }: { label?: string }) {
  return (
    <div class="state state--loading" role="status" aria-live="polite">
      <span class="spinner" aria-hidden="true" />
      <span>{label ?? t('common.loading')}</span>
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div class="state state--error" role="alert">
      <p>{message}</p>
      {onRetry ? (
        <button type="button" class="btn" onClick={onRetry}>
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ComponentChildren;
}) {
  return (
    <div class="state state--empty">
      <p class="state__title">{title}</p>
      {hint ? <p class="muted">{hint}</p> : null}
      {action}
    </div>
  );
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'ok';
  children: ComponentChildren;
}) {
  return (
    <p class={`notice notice--${tone}`} role={tone === 'warn' ? 'alert' : 'status'}>
      {children}
    </p>
  );
}

export function Field({
  label,
  hint,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  children: ComponentChildren;
  htmlFor?: string;
}) {
  return (
    <div class="field">
      <label class="field__label" for={htmlFor}>
        {label}
      </label>
      {children}
      {hint ? <p class="field__hint muted">{hint}</p> : null}
    </div>
  );
}

export function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ComponentChildren;
}) {
  return (
    <section class="card">
      <header class="card__header">
        <h2>{title}</h2>
        {description ? <p class="muted">{description}</p> : null}
      </header>
      <div class="card__body">{children}</div>
    </section>
  );
}

export function Badge({ tone, children }: { tone: 'ok' | 'bad' | 'neutral'; children: ComponentChildren }) {
  return <span class={`badge badge--${tone}`}>{children}</span>;
}
